<template>
  <div v-if="loading"><Loader /></div>
  <div v-else>
    <b-form-group v-if="mode === 'readonly'">
      <label for="forced-institution-name-input">{{ t("Institution") }}</label>

      <b-form-input
        id="forced-institution-name-input"
        v-model="forcedInstitutionName"
        type="text"
        readonly
        disabled
      />
    </b-form-group>

    <b-form-group v-else-if="mode === 'manualSlug'">
      <label for="manual-institution-slug-input"
        >{{ $t("Institution slug") }}

        <Tooltip
          :value="
            $t(
              `This is a unique string of characters located at the end of the registration link, after the '/' sign, e.g. in the address: https://xxx.obiado.pl/auth/register/ABC123 - the location code is ABC123`,
            )
          "
        />
      </label>

      <b-form-input
        id="manual-institution-slug-input"
        v-model="manualInstitutionSlug"
        :disabled="loadingInstitutions"
        :placeholder="t('Fill institution slug. If you don\'t know it please contact support')"
      />

      <label v-if="manualInstitutionName" for="found-institution-name-input" class="mt-3">{{
        t("Found institution")
      }}</label>

      <b-form-input
        v-if="manualInstitutionName"
        id="found-institution-name-input"
        v-model="manualInstitutionName"
        type="text"
        readonly
      />
    </b-form-group>

    <!-- Districts -->
    <b-form-group v-if="districts.length > 0 && mode === 'selector'">
      <DistrictsSelect v-model="selectedDistrictId" :default-options="districts" />
    </b-form-group>

    <!-- Institution -->
    <b-form-group v-if="mode === 'selector'">
      <InstitutionsSelect
        v-if="institutions?.length"
        v-model="selectedInstitutionInternal"
        :disabled="districts.length > 0 && !selectedDistrictId"
        :show-only-from-district="selectedDistrictId"
        :default-options="mergedInstitutions"
        :show-placeholder="false"
        :max-height="500"
        :show-all="true"
      />

      <!-- Fetched, but no institution is available for selection -->
      <b-alert v-else :model-value="true" variant="warning" class="mb-0">
        {{ $t("No institutions are currently available.") }}
      </b-alert>
    </b-form-group>
  </div>
</template>

<script lang="ts" setup>
import InstitutionsSelect from "@/modules/institutions/components/InstitutionsSelect.vue";
import { useStore } from "vuex";
import { useRouteParams } from "@vueuse/router";
import { useI18n } from "vue-i18n";
import { ref, computed, watch, type ComputedRef } from "vue";
import DistrictsSelect from "@/modules/districts/components/DistrictsSelect.vue";
import Loader from "@/components/Loader.vue";
import { useShowNotification } from "@/composables/showNotification.ts";
import { useInstitutionOptions } from "@/composables/data/useInstitutionOptions";
import { useDistrictsOptions } from "@/composables/data/useDistrictsOptions";
import Tooltip from "@/components/Tooltip.vue";
import {
  hideInstitutionsListsFromParents,
  allowChangingInstitutions,
} from "@/modules/settings/queries/tenantSettingsSelectors";

const { t } = useI18n();
const store = useStore();

const canChangeInstitution = computed(() => {
  const isParent = store.getters["auth/isParent"] as boolean;
  const canEditChildren = store.getters["auth/canEditChildren"] as boolean;

  if (canEditChildren && !isParent) {
    return true;
  }

  return allowChangingInstitutions.value;
});

const institutionSlug = useRouteParams<string | undefined>("institutionSlug");

const { showNotification, clearNotification } = useShowNotification();

type InstitutionsHideableSelectMountMode = "edit" | "create" | undefined;

const props = withDefaults(
  defineProps<{
    selectedInstitution: number | undefined | null;
    mountMode?: InstitutionsHideableSelectMountMode;
    defaultInstitutionId?: number | null;
  }>(),
  {
    mountMode: "edit",
    defaultInstitutionId: null,
  },
);

const emit = defineEmits<{
  "update:selectedInstitution": [value: number | undefined];
  clearInstitutionRelatedData: [];
}>();

const { institutions, loading: loadingInstitutions } = useInstitutionOptions();

const { districts, loading: loadingDistricts } = useDistrictsOptions();

const loading = computed(() => loadingInstitutions.value || loadingDistricts.value);

type InsitutionPartialType = { slug: string; value: number; text?: string };

// When editing an existing child whose current institution has since been hidden
// for users (or archived out of the default active list), it must stay visible in
// the selector as the current value, alongside all other currently visible institutions.
const currentInstitutionFallback = ref<InsitutionPartialType[]>([]);

const mergedInstitutions = computed<InsitutionPartialType[]>(() => {
  if (!currentInstitutionFallback.value.length) {
    return institutions.value;
  }

  const visibleInstitutionIds = new Set(
    institutions.value.map((institution: InsitutionPartialType) => institution.value),
  );

  const fallbackOnly = currentInstitutionFallback.value.filter(
    (institution) => !visibleInstitutionIds.has(institution.value),
  );

  return [...institutions.value, ...fallbackOnly];
});

watch(
  [loading, () => props.selectedInstitution],
  async ([isLoading, selectedInstitutionId]) => {
    if (isLoading || props.mountMode !== "edit" || !selectedInstitutionId) {
      return;
    }

    const alreadyVisible = institutions.value.some(
      (institution: InsitutionPartialType) => institution.value === selectedInstitutionId,
    );

    const alreadyFetched = currentInstitutionFallback.value.some(
      (institution: InsitutionPartialType) => institution.value === selectedInstitutionId,
    );

    if (alreadyVisible || alreadyFetched) {
      return;
    }

    const result = await store.dispatch("institutions/getOptions", {
      institutionsIds: [selectedInstitutionId],
    });

    if (Array.isArray(result) && result.length) {
      currentInstitutionFallback.value = result;
    }
  },
  { immediate: true },
);

const selectedDistrictId = ref();

const selectedInstitutionInternal = computed({
  get: () => props.selectedInstitution,
  set: (value: number | undefined) => {
    emit("update:selectedInstitution", value);
  },
});

const manualInstitutionSlug = ref();
const manualInstitutionName = ref();
let manualInstitutionSlugDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const districtsIndex = computed(() => {
  const index: Record<number, number> = {};
  for (const institution of institutions.value) {
    index[institution.value] = institution.districtId;
  }
  return index;
});

const clearInputs = () => {
  manualInstitutionSlug.value = undefined;
  manualInstitutionName.value = undefined;
  selectedInstitutionInternal.value = undefined;
  selectedDistrictId.value = undefined;
};

const forcedInstitutionName = computed(() => {
  if (institutionSlug.value === undefined) {
    return null;
  }

  if (institutions.value.length === 0) {
    // called too early ?
    return null;
  }

  const forcedInstitution = institutions.value.find(
    (institution: InsitutionPartialType) => institution.slug === institutionSlug.value,
  );

  return forcedInstitution?.text || t("Institution not found");
});

const mode: ComputedRef<"readonly" | "manualSlug" | "selector" | "hidden"> = computed(() => {
  const isAdmin = store.getters["auth/isAdmin"];

  const isInstitutionsAdmin = store.getters["auth/isInstitutionsAdmin"];

  // Admins: always selector
  if (isAdmin) {
    return "selector";
  }

  // Institutions admins (with children_edit) see selector
  if (isInstitutionsAdmin && store.getters["auth/permissions"].includes("children_edit")) {
    return "selector";
  }

  const canChangeByAuth = canChangeInstitution.value;

  // Parents specific rules
  if (store.getters["auth/isParent"]) {
    // If route has forced slug
    if (institutionSlug.value !== undefined) {
      return "readonly";
    }

    // Parent canChangeByAuth OR parent IS IN CREATE CHILD VIEW
    if (canChangeByAuth || (!canChangeByAuth && props.mountMode === "create")) {
      // Institutions list is hidden from parent - they must provide slug
      if (hideInstitutionsListsFromParents.value) {
        return "manualSlug";
      }

      return "selector";
    }

    // Parent cannot change by auth AND parent IS IN EDIT CHILD VIEW
    if (!canChangeByAuth && props.mountMode === "edit") {
      // Hide this field
      return "hidden";
    }
  }

  // Other cases is hidden
  return "hidden";
});

const refreshManualInstitutionSlug = () => {
  // need institutions to be loaded so we can find actual slug
  if (!institutions.value || institutions.value.length === 0) {
    return;
  }

  if (!selectedInstitutionInternal.value) {
    return;
  }

  if (
    store.getters["auth/isParent"] &&
    canChangeInstitution.value &&
    hideInstitutionsListsFromParents.value
  ) {
    manualInstitutionSlug.value = institutions.value.find(
      (institution: InsitutionPartialType) =>
        institution.value === selectedInstitutionInternal.value,
    )?.slug;
  }
};

const clearInstitutionRelatedData = () => {
  emit("clearInstitutionRelatedData");
};

const trySetInstitutionFromInstitutionSlug = () => {
  const slug = (institutionSlug.value || "").trim();

  if (slug.length === 0) {
    return;
  }

  const institution = institutions.value.find(
    (institution: InsitutionPartialType) => institution.slug === slug,
  );

  if (!institution) {
    return;
  }

  selectedInstitutionInternal.value = institution.value;
  manualInstitutionName.value = institution.text;
};

const trySetInstitutionFromManualSlug = () => {
  if (!Array.isArray(institutions.value) || institutions.value.length === 0) {
    return;
  }

  const slug = (manualInstitutionSlug.value || "").trim();

  if (slug.length === 0) {
    return;
  }

  const institution = institutions.value.find((i) => i.slug === slug);

  if (!institution) {
    // Clear current selection while user types
    clearInstitutionRelatedData();

    showNotification({
      type: "warning",
      text: t("Error occured. Institution with given slug was not found."),
    });
    return;
  }

  clearNotification();

  selectedInstitutionInternal.value = institution.value;
  manualInstitutionName.value = institution.text;
};

watch(
  selectedInstitutionInternal,
  (value) => {
    if (value === undefined || value === null) {
      return;
    }

    refreshManualInstitutionSlug();

    selectedDistrictId.value = districtsIndex.value[value];
  },
  { immediate: true },
);

// there are some cases when districtsIndex is not yet loaded when selectedInstitutionInternal is already set
watch(districtsIndex, (value) => {
  if (value === undefined || value === null) {
    return;
  }

  if (
    selectedInstitutionInternal.value === undefined ||
    selectedInstitutionInternal.value === null
  ) {
    return;
  }

  selectedDistrictId.value = districtsIndex.value[selectedInstitutionInternal.value];
});

watch(manualInstitutionSlug, (newValue) => {
  // Debounce: run lookup 1s after last keystroke
  if (manualInstitutionSlugDebounceTimer) {
    clearTimeout(manualInstitutionSlugDebounceTimer);
    manualInstitutionSlugDebounceTimer = null;
  }

  // If field is empty, nothing to do
  if (!newValue || newValue.trim().length === 0) {
    return;
  }

  manualInstitutionSlugDebounceTimer = setTimeout(() => {
    trySetInstitutionFromManualSlug();
  }, 1000);
});

watch(
  () => props.selectedInstitution,
  (newValue) => {
    if (newValue === null || newValue === undefined) {
      clearInputs();
    }
  },
);

watch(
  loading,
  (newValue) => {
    if (newValue === false) {
      trySetInstitutionFromInstitutionSlug();

      // Pre-select institution from defaultInstitutionId prop if provided and no selection yet
      if (
        props.defaultInstitutionId &&
        !selectedInstitutionInternal.value &&
        !institutionSlug.value
      ) {
        selectedInstitutionInternal.value = props.defaultInstitutionId;
      }
    }
  },
  { immediate: true },
);
</script>
